import { CircularProgress, Grid, IconButton, InputAdornment, makeStyles, TextField, Theme } from '@material-ui/core';
import { StyleRules } from '@material-ui/core/styles';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import React, { useEffect, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';

import Button from '../../../../components/CustomButtons/Button';
import FormCard from '../../../../components/FormCard/FormCard';
import GridRow from '../../../../components/NewUI/GridRow';
import Label from '../../../../components/NewUI/Label/Label';
import SettingsContainer from '../../../../components/NewUI/SettingsContainer';

import * as commonActions from '../../../../store/actions/common_actions';
import * as nexodusActions from '../../../../store/actions/nexodus_actions';
import { IState } from '../../../../store/reducers';
import { IStyleProps, PropsClasses } from '../../../../types/StyleProps';

import style from './styles';
import { Props, StateProps } from './types';

const useStyles = makeStyles<Theme, IStyleProps>(style as StyleRules);

const NexodusApp = (props: Props) => {
  const classes: PropsClasses = useStyles({} as IStyleProps);
  const {
    translate,
    nexodusSettings,
    connectionStatus,
    isFetchingConnect,
    isFetchingWebhook,
    connectError,
    getSettingsRequest,
    putSettingsRequest,
    disconnectRequest,
    nexodusConnectRequest,
    nexudusWebhookRequest,
  } = props;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [spaceId, setSpaceId] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  useEffect(() => {
    getSettingsRequest({ system: 'nexudus' });
  }, []);

  useEffect(() => {
    if (nexodusSettings) {
      setSpaceId(nexodusSettings.spaceId || '');
      setWebhookSecret(nexodusSettings.webhookSecret || '');
    }
  }, [nexodusSettings]);

  const handleConnect = () => {
    nexodusConnectRequest({ email, password });
  };

  const handleDisconnect = () => {
    disconnectRequest({ system: 'nexudus' });
  };

  const handleSave = () => {
    putSettingsRequest({
      system: 'nexudus',
      data: {
        spaceId,
        webhookSecret,
        webhookUrl: `${process.env.REACT_APP_BE_URL}/nexudus/webhook/invoice`,
      },
    });
  };

  const isConnected = connectionStatus === 'ok';
  const isLoading = connectionStatus === 'loading';

  const webhookActive = !!(
    nexodusSettings &&
    nexodusSettings.activeWebhooks &&
    nexodusSettings.activeWebhooks.some(wh => wh.URL && wh.URL.includes('/nexudus/webhook/invoice'))
  );

  return (
    <SettingsContainer appName={translate('nexudus_app_name') as string}>
      <GridRow container={true}>
        <Grid item={true} xs={12} md={4}>
          <Label title={translate('status') as string} desc="" />
        </Grid>
        <Grid item={true} xs={12} md={8}>
          <FormCard>
            {isLoading ? (
              <CircularProgress />
            ) : isConnected ? (
              <div className={classes.connectedContainer}>
                <span className={classes.connectedStatus}>{translate('connected') as string}</span>
                <Button color="danger" size="sm" onClick={handleDisconnect}>
                  {translate('disconnect') as string}
                </Button>
              </div>
            ) : (
              <div>
                <TextField
                  label={translate('nexudus.email') as string}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                  type="email"
                />
                <TextField
                  label={translate('nexudus.password') as string}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {connectError && (
                  <p style={{ color: '#f44336', marginTop: 8 }}>{translate('nexudus.connect_error') as string}</p>
                )}
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleConnect}
                  disabled={isFetchingConnect || !email || !password}
                  style={{ marginTop: 8 }}
                >
                  {isFetchingConnect ? <CircularProgress size={16} /> : (translate('connect') as string)}
                </Button>
              </div>
            )}
          </FormCard>
        </Grid>
      </GridRow>

      {isConnected && (
        <>
          <GridRow container={true}>
            <Grid item={true} xs={12} md={4}>
              <Label title={translate('nexudus.space_id') as string} desc="" />
            </Grid>
            <Grid item={true} xs={12} md={8}>
              <FormCard>
                <TextField
                  value={spaceId}
                  onChange={e => setSpaceId(e.target.value)}
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                />
              </FormCard>
            </Grid>
          </GridRow>

          <GridRow container={true}>
            <Grid item={true} xs={12} md={4}>
              <Label title={translate('nexudus.webhook_secret') as string} desc="" />
            </Grid>
            <Grid item={true} xs={12} md={8}>
              <FormCard>
                <TextField
                  value={webhookSecret}
                  onChange={e => setWebhookSecret(e.target.value)}
                  fullWidth={true}
                  margin="dense"
                  variant="outlined"
                  type={showWebhookSecret ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowWebhookSecret(!showWebhookSecret)}>
                          {showWebhookSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormCard>
            </Grid>
          </GridRow>

          <GridRow container={true}>
            <Grid item={true} xs={12} md={4}>
              <Label title={translate('nexudus.webhook_status') as string} desc="" />
            </Grid>
            <Grid item={true} xs={12} md={8}>
              <FormCard>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span
                    className={`${classes.webhookBadge} ${
                      webhookActive ? classes.webhookActive : classes.webhookInactive
                    }`}
                  >
                    {webhookActive
                      ? (translate('nexudus.webhook_active') as string)
                      : (translate('nexudus.webhook_inactive') as string)}
                  </span>
                  {webhookActive ? (
                    <Button
                      color="danger"
                      size="sm"
                      disabled={isFetchingWebhook}
                      onClick={() => {
                        const ids = (nexodusSettings ? nexodusSettings.activeWebhooks : [])
                          .filter(wh => wh.URL && wh.URL.includes('/nexudus/webhook/invoice'))
                          .map(wh => wh.Id);
                        nexudusWebhookRequest({ ids });
                      }}
                    >
                      {isFetchingWebhook ? (
                        <CircularProgress size={16} />
                      ) : (
                        (translate('nexudus.remove_webhook') as string)
                      )}
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      size="sm"
                      disabled={isFetchingWebhook}
                      onClick={() =>
                        nexudusWebhookRequest({
                          webhookUrl: `${process.env.REACT_APP_BE_URL}/nexudus/webhook/invoice`,
                        })
                      }
                    >
                      {isFetchingWebhook ? (
                        <CircularProgress size={16} />
                      ) : (
                        (translate('nexudus.register_webhook') as string)
                      )}
                    </Button>
                  )}
                </div>
              </FormCard>
            </Grid>
          </GridRow>

          <GridRow container={true}>
            <Grid item={true} xs={12} md={4} />
            <Grid item={true} xs={12} md={8}>
              <Button color="primary" onClick={handleSave}>
                {translate('save') as string}
              </Button>
            </Grid>
          </GridRow>
        </>
      )}
    </SettingsContainer>
  );
};

const mapStateToProps = (state: IState): StateProps => ({
  nexodusSettings: state.nexudus.settings,
  connectionStatus: state.nexudus.connectionStatus,
  isFetchingConnect: state.nexudus.isFetchingConnect,
  isFetchingWebhook: state.nexudus.isFetchingWebhook,
  connectError: state.nexudus.connectError,
});

const mapDispatchToProps = {
  getSettingsRequest: commonActions.getSettingsRequest,
  putSettingsRequest: commonActions.putSettingsRequest,
  disconnectRequest: commonActions.disconnectRequest,
  nexodusConnectRequest: nexodusActions.nexodusConnectRequest,
  nexudusWebhookRequest: nexodusActions.nexudusWebhookRequest,
};

export default withLocalize(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(NexodusApp),
);
